import {
  DIAGRAM_PITCH,
  DIAGRAM_PITCH_LABELS,
  DIAGRAM_SYMBOL_SCALE,
  DIAGRAM_SYMBOL_SCALE_LABELS,
  type DiagramPitch,
  type DiagramSymbolScale,
} from "@ukinfo/shared";
import { useState, type ReactNode } from "react";
import { Field, Select } from "../ui";
import { ToolButton } from "./DiagramTools";
import { ZoomControls, type DiagramViewport } from "./useDiagramViewport";

/**
 * Bereich "Ansicht" des Skizzeneditors, unter dem Feld: alles, was nur das Aussehen und die Hilfen beim Setzen betrifft - Zoom,
 * Einrasten, Raster, Schrittnummern ein/aus, Feldart, Symbolgroesse und die Vorschau "Ablauf ansehen". Dezent gehalten, getrennt vom Bearbeiten.
 * Einklappbar (je Geraet gemerkt): eingeklappt bleibt nur die Kopfzeile mit dem Zoom, damit der Bereich "Auswahl" naeher am Feld liegt.
 */
export const VIEW_OPEN_KEY = "ukinfo.skizze.ansicht";

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(VIEW_OPEN_KEY) !== "0";
  } catch {
    // Ohne Speicher (privater Modus, gesperrt) bleibt der Bereich offen.
    return true;
  }
}

export function ViewBar({
  viewport,
  pitch,
  symbolScale,
  snap,
  grid,
  steps,
  gridInfo,
  hasArrows,
  disabled,
  onPitch,
  onSymbolScale,
  onToggleSnap,
  onToggleGrid,
  onToggleSteps,
  onPreview,
  panel = false,
  showZoom = true,
}: {
  /** Im Vollbild-Popover: immer offen, ohne Einklappen (und ohne eigenen Zoom, wenn die Kopfzeile ihn zeigt). */
  panel?: boolean;
  showZoom?: boolean;
  viewport: DiagramViewport;
  pitch: DiagramPitch;
  symbolScale: DiagramSymbolScale;
  snap: boolean;
  grid: boolean;
  /** Schrittnummern an den Pfeilen im Feld zeigen (nur die Ansicht des Editors, nichts wird gespeichert). */
  steps: boolean;
  /** Rasterweite in Metern fuer den Hinweis unter dem Raster (nur bei eingeschaltetem Raster). */
  gridInfo: ReactNode;
  hasArrows: boolean;
  disabled?: boolean;
  onPitch: (pitch: DiagramPitch) => void;
  onSymbolScale: (scale: DiagramSymbolScale) => void;
  onToggleSnap: () => void;
  onToggleGrid: () => void;
  onToggleSteps: () => void;
  onPreview: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(readOpen);
  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(VIEW_OPEN_KEY, next ? "1" : "0");
    } catch {
      // Nicht merken ist kein Fehler.
    }
  };
  return (
    <section
      aria-label="Ansicht"
      data-testid="view-bar"
      className="w-full space-y-2 rounded-xl border border-slate-200 bg-white/70 p-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        {panel ? (
          <p className="px-2 text-[0.68rem] font-semibold uppercase tracking-wide text-slate-600">Ansicht</p>
        ) : (
        <button
          type="button"
          aria-expanded={open}
          title={open ? "Ansicht einklappen" : "Ansicht ausklappen"}
          onClick={toggle}
          className="flex min-h-11 items-center gap-1.5 rounded-lg px-2 text-[0.68rem] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
        >
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`}
          >
            <path
              d="M6 3.5 10.5 8 6 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Ansicht
        </button>
        )}
        {showZoom && <ZoomControls viewport={viewport} />}
      </div>
      {(open || panel) && (
        <div className="space-y-2">
          <div className="flex items-center justify-end gap-1.5">
            <ToolButton
              label="Einrasten"
              short="Einrasten"
              icon="snap"
              pressed={snap}
              disabled={disabled}
              onClick={onToggleSnap}
            />
            <ToolButton
              label="Raster"
              short="Raster"
              icon="grid"
              pressed={grid}
              disabled={disabled}
              onClick={onToggleGrid}
            />
            <ToolButton
              label="Schritte"
              short="Schritte"
              icon="steps"
              pressed={steps}
              disabled={disabled}
              onClick={onToggleSteps}
            />
          </div>
          {gridInfo}
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[8rem] flex-1">
              <Field label="Feld">
                {(id) => (
                  <Select
                    id={id}
                    value={pitch}
                    disabled={disabled}
                    onChange={(event) =>
                      onPitch(event.target.value as DiagramPitch)
                    }
                  >
                    {DIAGRAM_PITCH.map((entry) => (
                      <option key={entry} value={entry}>
                        {DIAGRAM_PITCH_LABELS[entry]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            <div className="min-w-[7rem] flex-1">
              <Field label="Symbole">
                {(id) => (
                  <Select
                    id={id}
                    value={String(symbolScale)}
                    disabled={disabled}
                    onChange={(event) =>
                      onSymbolScale(
                        Number(event.target.value) as DiagramSymbolScale,
                      )
                    }
                  >
                    {DIAGRAM_SYMBOL_SCALE.map((entry) => (
                      <option key={entry} value={String(entry)}>
                        {DIAGRAM_SYMBOL_SCALE_LABELS[entry]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            <ToolButton
              label="Ablauf ansehen"
              short="Ablauf ansehen"
              icon="play"
              layout="inline"
              disabled={!hasArrows}
              onClick={onPreview}
            />
          </div>
          {!hasArrows && (
            <p className="text-xs text-slate-500">
              „Ablauf ansehen“ spielt die Pfeile Schritt für Schritt ab – sobald
              die Skizze Pfeile hat.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
